import React, { useCallback, useEffect, useState } from 'react';
import { Box, Chip, Grid, IconButton, Typography } from "@mui/material";

import {
    BreadCrumbs,
    Table,
    Column,
    PageTitleWithToolTip,
    CheckBox,
    Input,
    Select,
    LoadingBar,
    ExitConfirmationModal,
    LoadingButtonV4,
} from "../../Common";
import { postRequestHeaders, trakrefFontFamily } from '../../../utils';

import Routes from '../../../routes';
import { useBreadCrumbs, useNavigate } from '../../../hooks';
import { monitor_status_options, selectRoundStyle, selectRoundStyleSelected } from '../../AddAsset/utils';
import toast from 'react-hot-toast';
import CloseIcon from '@mui/icons-material/Close';
import { Translate } from '../../../Utils/Languages/Translate';
import { Grey1 } from '../../../color_utils';

const initialValue = {
    coolingApplianceId : null,
    isLeakMonitored: true,
    isPrimaryMonitor: null,
    refrigerantLeakMonitorSensorId: null
};

const LinkCoolingAppliances = ({
    history,
    match: {
        params: { id } = {},
    },
    }
) => {
    const [loading, setLoading] = useState(true);
    const [sensorDropdown, setSensorDropdown] = useState([]);
    const [updatedLinkAppliance, setUpdatedLinkAppliance] = useState([]);

    const [tableData, SetTableData] = useState([]);
    const [updatedDataTable, SetUpdatedDataTable] = useState([]);
    const [exitModal, setExitModal] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const navigate = useNavigate({
        pageName: 'LinkCoolingAppliances'
    });

    const breadCrumbs = useBreadCrumbs({
        path: { id: 'LinkCoolingAppliances', text: `Link Cooling Appliances`, link: history.location.pathname, shouldBeReplaced: true}
    });

    const fetchData = async () => {
        const response = await fetch(Routes.Asset.GetLinkCoolingApplianceDropdown(id));
        const data = await response.json();
        setSensorDropdown(data.sensorsDropdown);
        setUpdatedLinkAppliance(data.linkedAppliances);
        setLoading(false);
    };
    const updateTableData = () => {
        if (!tableData || !tableData.rows || tableData.rows.length === 0) {
          return;
        }
        const updatedData = tableData.rows.map((item) => {
          
        const updatedItem = updatedLinkAppliance.find(
            (updated) => {
                return updated.coolingApplianceId === item.coolingApplianceId;
            }
        );

          if (updatedItem) {
            return { ...item, ...updatedItem };
          }
          return item;
        });

        SetUpdatedDataTable(updatedData);
    };

    const isPrimaryMonitorData = (value) => {
        // using 0 or false as dropdown value result in dropdown option not selected
        if (value === 1) {
            return true;
        } else if (value === 2) {
            return false;
        }
        return null;
    };

    const handleChange = (column, selectedValue, row) => {
        selectedValue = column === "isPrimaryMonitor" ? isPrimaryMonitorData(selectedValue) : selectedValue;

        setUpdatedLinkAppliance((prevData) => {
          const existingRow = prevData.find(item => item.coolingApplianceId === row.coolingApplianceId);
          const shouldClearData = column === "isLeakMonitored" && selectedValue === false;

          if (existingRow) {
            return prevData.map(item =>
              (item.coolingApplianceId === row.coolingApplianceId
                ? { ...item, 
                    [column]: selectedValue,
                    ...(shouldClearData ? 
                        {
                            "isPrimaryMonitor": null,
                            "refrigerantLeakMonitorSensorId": null
                        }
                     : {})
                }
                : item)
            );
          } else {
            // If not exists, add new row with `initialValue` as base and update column dynamically
            return [
              ...prevData,
              {
                ...initialValue, 
                ...row, 
                [column]: selectedValue,
                isLeakMonitored: true
              }
            ];
          }
        });
      };

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        updateTableData();
    }, [tableData, updatedLinkAppliance]);

    const onSubmit = async () => {
        try {
            setIsSaving(true);
            const response = await fetch(Routes.Asset.UpdateLinkCoolingAppliance(), {
                method: 'POST',
                headers: { ...postRequestHeaders },
                body: JSON.stringify({
                    LeakMonitorID: id,
                    UpdatedAppliances: updatedLinkAppliance
                })
            });
    
            if (response.status === 200) {
                toast.success("The cooling appliances have been successfully linked.", { duration: 6000 });
            } else {
                toast.error('There was an error saving the cooling appliances', { duration: 6000 });
            }
        } catch (error) {
            toast.error('There was an error saving the cooling appliances', { duration: 6000 });
            setIsSaving(false);
        } finally {
            setIsSaving(false);
            navigate.push(`/Inventory/${id}`, null, { activeTab: 3});
            
        }
    };

    if (loading) {
        return <LoadingBar />;
    }

    return (
        <>
            <BreadCrumbs
                path={breadCrumbs.getSortedBreadCrumbs()}
            />
            <Grid
                container
                direction="row"
                alignItems="center"
            >
                <Grid item xs={6} md={6} lg={6} mb={2} sx={{ display: "flex", alignItems: "center"}} >
                    <IconButton aria-label="close" onClick={() => setExitModal(true)}>
                        <CloseIcon />
                    </IconButton>
                    <PageTitleWithToolTip pageTitle="Link Cooling Appliances"/>
                </Grid>
                <Grid item xs={6} md={6} lg={6} align="right">
                    <LoadingButtonV4
                        buttonText={"Save"}
                        width={"16"}
                        onClick={() => onSubmit()}
                        loading={isSaving}
                    />
                </Grid>

                <Grid item xs={12} md={12} lg={12} ml={5}>
                    <Typography
                        sx={{
                            fontSize: "13px", 
                            color: Grey1, 
                            fontFamily: trakrefFontFamily
                        }}
                    >
                        {Translate('assetDetailsLinkCoolingAppliance.description')}
                    </Typography>
                </Grid>

                <Grid item xs={12} md={12} lg={12}>
                    <Table
                        renderDefaultFilter
                        showColumnHeaderFilter
                        filterOptions={[]}
                        rowActions={[]}
                        url={Routes.Asset.GetLinkCoolingApplianceTable(id)}
                        noResultText="Oops... It's empty here..."
                        DataCallback={(data) => {
                            SetTableData(data);
                        }}
                        updatedRow={updatedDataTable}
                    >
                        <Column
                          hideColumnName={true}
                          id="isLeakMonitored"
                          title="Is LeakMonitored"
                          property="isLeakMonitored"
                          render={(row) => (
                            <CheckBox 
                                checked={row.isLeakMonitored}
                                onChange={(e) => {
                                    handleChange("isLeakMonitored", e.target.checked, row);
                                }}
                            />
                          )}
                          width="16px"
                        />
                        <Column id='coolingApplianceName' title="Cooling Appliance Name" property="coolingApplianceName" filter="text" width="35%"/>
                        <Column
                          id="isPrimaryMonitor"
                          title="Monitor Status"
                          property="isPrimaryMonitor"
                          width="30%"
                          render={(row) => (
                            <Select 
                                options={monitor_status_options}
                                onChange={(e) => {
                                    handleChange("isPrimaryMonitor", e.target.value, row);
                                }}
                                variant={"standard"}
                                value={typeof row.isPrimaryMonitor === 'boolean' 
                                    ? row.isPrimaryMonitor 
                                      ? 1
                                      : 2
                                    : row.isPrimaryMonitor}
                                customCreateNewItemText="Select Status"
                                displayEmpty
                                disableUnderline
                                renderValue={(selected) => {
                                    const selectedOption = monitor_status_options.find((option) => option.value === selected);
                                    return  <Box>
                                                {selectedOption ? selectedOption.label : "Select Status"}
                                            </Box>;
                                }}
                                sx={(typeof row.isPrimaryMonitor === 'boolean' 
                                    ? row.isPrimaryMonitor 
                                      ? 1
                                      : 2
                                    : row.isPrimaryMonitor) ? selectRoundStyleSelected : selectRoundStyle}
                            />
                          )}
                        />
                        <Column 
                            id='refrigerantLeakMonitorSensorId' 
                            title="Assigned Sensor" 
                            property="refrigerantLeakMonitorSensorId" 
                            render={(row) => (
                                <Select 
                                    onChange={(e) => {
                                        handleChange("refrigerantLeakMonitorSensorId", e.target.value, row);
                                    }}
                                    variant={"standard"}
                                    value={row.refrigerantLeakMonitorSensorId}
                                    options={sensorDropdown}
                                    customCreateNewItemText={"Select Sensor"}
                                    displayEmpty
                                    disableUnderline
                                    renderValue={(selected) => {
                                        const selectedOption = sensorDropdown.find((option) => option.id === selected);
                                        return  <Box>
                                                    {selected ? selectedOption.name : "Select Sensor"}
                                                </Box>;
                                    }}
                                    sx={row.refrigerantLeakMonitorSensorId ? selectRoundStyleSelected : selectRoundStyle}
                                />
                            )}
                            width="30%"
                        />
                    </Table>
                </Grid>
            </Grid>

            <ExitConfirmationModal
                open={exitModal}
                close={() => setExitModal(false)}
                leaveOnClick={() => navigate.goBack()}
                stayOnClick={() => setExitModal(false)}
            />
        </>
    );
};

export default LinkCoolingAppliances;
